import { buildStationChain } from 'pages/WorkAssignments/components/WorkAssignmentCard/utils';

const stationLabel = (station) =>
	station?.name ? `${station.code} - ${station.name}` : (station?.code ?? '');

/**
 * Derives the segment dropdown options for Step 1 from an assignment.
 *
 * A "segment" is a consecutive pair of stations from the assignment's full
 * station chain (which is built by walking every sequence's `ride.stations`
 * and deduping the overlaps where one sequence ends where the next begins).
 * For a chain `[A, B, C, D]` we emit three options with labels like
 * `A - Alpha → B - Bravo`; each station falls back to its code alone if
 * `name` is missing.
 *
 * Each option carries the segment's wall-clock window:
 *   - `fromTimestamp` = the from-station's `departure`
 *   - `toTimestamp`   = the to-station's `arrival`
 * which downstream Step 1 validation uses to constrain `incidentTimestamp`.
 *
 * @param {Object} [assignment]
 * @returns {Array<{
 *   id: string,
 *   label: string,
 *   fromTimestamp: string,
 *   toTimestamp: string,
 *   fromStation: Object,
 *   toStation: Object,
 * }>}
 */
export const deriveSegmentOptions = (assignment) => {
	const chain = buildStationChain(assignment?.workAssignmentSequences);
	if (chain.length < 2) return [];

	const options = [];
	for (let i = 0; i < chain.length - 1; i += 1) {
		const fromStation = chain[i];
		const toStation = chain[i + 1];
		if (!fromStation?.id || !toStation?.id) continue;
		options.push({
			id: `${fromStation.id}->${toStation.id}`,
			fromStation,
			toStation,
			fromTimestamp: fromStation.departure,
			toTimestamp: toStation.arrival,
			label: `${stationLabel(fromStation)} → ${stationLabel(toStation)}`,
		});
	}
	return options;
};
